import { useCallback, useEffect, useMemo, useState } from 'react'
import { SearchIcon } from '@panwds/icons'
import { Input, List, ListItem } from '@panwds/react-ui'
import _ from 'lodash'
import fetch from '../../libs/fetch'
import { launchSingleTenantView } from '../../framework/actions'
import { getFrameworkServiceUrl, getFrameworkVar, stateRef } from '../../framework/states'
import { getAuthHeaders, getTsgInfo } from '../../framework/tsg'
import { API_GW_GLOBAL_SVC_URL_KEY, TSG_ID_NONE } from '../../framework/constants'
import { dedupPromise, ensureArray } from '../../framework/utils'
import { __error } from '../../init/debug'
import type { VFC, ChangeEvent, ReactNode, KeyboardEvent } from 'react'

const MAX_SEARCH_RESULT = 50
const TSG_ID_REGEX = /^1\d{9}$/

export type TsgNameMapping = Record<string, string>
const doFetchTsgNames = async (): Promise<TsgNameMapping> => {
  const baseUrl = getFrameworkServiceUrl(API_GW_GLOBAL_SVC_URL_KEY)!
  const url = new URL('/mfe/cached_tsg_names', baseUrl)
  try {
    const headers = await getAuthHeaders(TSG_ID_NONE)
    const res = await fetch(url.toString(), {
      method: 'GET',
      mode: 'cors',
      credentials: 'omit',
      timeout: 20000, // 20s
      headers,
    })
    if (res.ok) {
      const data = await res.json()
      if (data?.mapping) {
        return data.mapping
      }
      const msg = 'failed to fetch cached tsg names, invalid response data'
      __error(msg, data)
      throw new Error(msg)
    }
    const msg = `failed to fetch cached tsg names, invalid response code: ${res.status}`
    __error(msg, await res.text())
    throw new Error(msg)
  }
  catch (err) {
    const msg = 'failed to fetch cached tsg names'
    __error(msg, err)
    return {} as TsgNameMapping
  }
}
const fetchTsgNames = () => dedupPromise('tsg_names', doFetchTsgNames, { keepPromiseIn: 30000 })

const renderDisplayName = ({ tsg_id, display_name }: TsgItem): ReactNode => {
  if (display_name === tsg_id) {
    return display_name
  }
  const display = !display_name.endsWith(tsg_id) ? display_name :
    display_name.slice(0, -tsg_id.length).replace(/[\s-]+$/, '')
  return <>{display} <small className='twsf-opacity-75'>- {tsg_id}</small></>
}

export type TsgItem = { tsg_id: string, display_name: string }
const TsgSelector: VFC<{}> = () => {
  const [searchValue, setSearchValue] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(true)
  const [tsgNames, setTsgNames] = useState<TsgNameMapping>({})
  const [tsgItems, setTsgItems] = useState<TsgItem[]>([])
  useEffect(() => {
    fetchTsgNames().then(mapping => {
      setTsgNames(mapping)
      setLoading(false)
    })
  }, [])
  const defaultItems = useMemo(() => {
    const defaultList = ensureArray(getFrameworkVar('private_site_tsg_list') || getFrameworkVar('private_site_default_tsg')).filter(Boolean) as string[]
    const list = defaultList.map(tsg_id => ({ tsg_id, display_name: tsgNames[tsg_id] || tsg_id }))
    return list
  }, [tsgNames])

  const tryLaunch = useCallback((tsgId: string) => {
    setLoading(true)
    getTsgInfo(tsgId).then(() => {
      launchSingleTenantView(tsgId, getFrameworkVar('default_route') || '/')
    }, () => {
      setLoading(false)
      alert(`Not able to switch to the selected TSG ${tsgId}.`)
    })
  }, [])

  const tsgList = useMemo(() => {
    const currentTsg = stateRef.current.auth?.tsg_id
    const items = tsgItems.length ? tsgItems.map((tsg) => {
      const disabled = loading || tsg.tsg_id === currentTsg
      return <ListItem
        disabled={disabled}
        key={tsg.tsg_id}
        onClick={() => !disabled && tryLaunch(tsg.tsg_id)}
      >
        {renderDisplayName(tsg)}
      </ListItem>
    }) : <div className='twsf-text-center twsf-text-gray-600 twsf-m-2'>
      No Result Found
    </div>
    return <List addClassName='twsf-m-2'>
      {items}
    </List>
  }, [loading, tryLaunch, tsgItems])
  const search = useMemo(() => _.debounce((searchValue) => {
    if (!searchValue) {
      return setTsgItems(defaultItems)
    }
    const tsgIdMatched = TSG_ID_REGEX.test(searchValue) ? tsgNames[searchValue] : undefined
    if (tsgIdMatched) {
      return setTsgItems([{ tsg_id: searchValue, display_name: tsgIdMatched }])
    }
    const filteredItems = []
    for (const tsg_id of Object.keys(tsgNames)) {
      const display_name = tsgNames[tsg_id]
      if (display_name.toLowerCase().includes(searchValue)) {
        filteredItems.push({ tsg_id, display_name })
      }
      if (filteredItems.length >= MAX_SEARCH_RESULT) {
        break
      }
    }
    setTsgItems(filteredItems)
  }, 500), [defaultItems, tsgNames])
  useEffect(() => {
    search(searchValue.toLowerCase())
  }, [search, searchValue])
  const searchInput = useMemo(() => {
    const changeHandler = (event: ChangeEvent<HTMLInputElement>) => {
      setSearchValue(event.target.value)
    }
    const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter' && TSG_ID_REGEX.test(searchValue)) {
        tryLaunch(searchValue)
      }
      else if (event.key === 'Escape' && searchValue) {
        setSearchValue('')
      }
    }
    return <Input
      prefix={<SearchIcon className='twsf-ml-2' />}
      value={searchValue}
      addClassName='twsf-m-2'
      placeholder='Enter TSG ID or Search Name'
      autoFocus
      disabled={loading}
      onChange={changeHandler}
      onKeyDown={onKeyDown}
    />
  }, [searchValue, loading, tryLaunch])

  return <div className='twsf-h-[20rem]'>
    {searchInput}
    {tsgList}
  </div>
}

export default TsgSelector
